import React, { useState, useEffect } from 'react'
import { 
  Download,
  Search,
  Calendar,
  Filter,
  RotateCcw,
  Eye,
  Edit,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Users,
  CheckCircle,
  Clock,
  AlertTriangle,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react'

const SalesPerformanceReport = () => {
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: 'All Status',
    priority: 'All Priority',
    manager: 'All Managers'
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [salesData, setSalesData] = useState([])

  // Dummy real-world sales data
  const dummySalesData = [
    {
      id: 'SL-001',
      leadName: 'TechCorp Enterprise Solutions',
      prospectName: 'John Smith',
      status: 'Negotiation',
      priority: 'High',
      manager: 'Sarah Johnson',
      leadValue: 250000,
      actualValue: 240000,
      variance: -10000,
      progress: 85,
      startDate: '15/01/2024',
      endDate: '30/06/2024',
      health: 'Good',
      risks: 1,
      activities: 12,
      teamSize: 3,
      conversionProbability: 75
    },
    {
      id: 'SL-002',
      leadName: 'Global Manufacturing Ltd',
      prospectName: 'Maria Garcia',
      status: 'Proposal Sent',
      priority: 'Medium',
      manager: 'Michael Chen',
      leadValue: 180000,
      actualValue: 175000,
      variance: -5000,
      progress: 70,
      startDate: '20/02/2024',
      endDate: '15/08/2024',
      health: 'Fair',
      risks: 2,
      activities: 8,
      teamSize: 2,
      conversionProbability: 60
    },
    {
      id: 'SL-003',
      leadName: 'Digital Innovations Inc',
      prospectName: 'David Wilson',
      status: 'Demo Scheduled',
      priority: 'High',
      manager: 'Sarah Johnson',
      leadValue: 320000,
      actualValue: 300000,
      variance: -20000,
      progress: 45,
      startDate: '10/03/2024',
      endDate: '30/09/2024',
      health: 'Excellent',
      risks: 0,
      activities: 15,
      teamSize: 4,
      conversionProbability: 85
    },
    {
      id: 'SL-004',
      leadName: 'Retail Chain Solutions',
      prospectName: 'Lisa Anderson',
      status: 'Qualified',
      priority: 'Low',
      manager: 'Alex Rodriguez',
      leadValue: 120000,
      actualValue: 115000,
      variance: -5000,
      progress: 30,
      startDate: '05/04/2024',
      endDate: '20/10/2024',
      health: 'Good',
      risks: 1,
      activities: 6,
      teamSize: 2,
      conversionProbability: 45
    },
    {
      id: 'SL-005',
      leadName: 'Healthcare Systems Pro',
      prospectName: 'Dr. Robert Brown',
      status: 'Converted',
      priority: 'High',
      manager: 'Sarah Johnson',
      leadValue: 450000,
      actualValue: 450000,
      variance: 0,
      progress: 100,
      startDate: '01/01/2024',
      endDate: '15/05/2024',
      health: 'Excellent',
      risks: 0,
      activities: 20,
      teamSize: 5,
      conversionProbability: 100
    },
    {
      id: 'SL-006',
      leadName: 'Financial Services Group',
      prospectName: 'Jennifer Lee',
      status: 'Lost',
      priority: 'Medium',
      manager: 'Michael Chen',
      leadValue: 200000,
      actualValue: 0,
      variance: -200000,
      progress: 0,
      startDate: '15/02/2024',
      endDate: '30/07/2024',
      health: 'Poor',
      risks: 3,
      activities: 10,
      teamSize: 3,
      conversionProbability: 0
    },
    {
      id: 'SL-007',
      leadName: 'E-commerce Platform',
      prospectName: 'Mark Thompson',
      status: 'Contacted',
      priority: 'Medium',
      manager: 'Alex Rodriguez',
      leadValue: 150000,
      actualValue: 140000,
      variance: -10000,
      progress: 25,
      startDate: '25/03/2024',
      endDate: '15/11/2024',
      health: 'Fair',
      risks: 2,
      activities: 4,
      teamSize: 2,
      conversionProbability: 35
    },
    {
      id: 'SL-008',
      leadName: 'Logistics & Supply Chain',
      prospectName: 'Amanda Davis',
      status: 'New',
      priority: 'Low',
      manager: 'Michael Chen',
      leadValue: 95000,
      actualValue: 90000,
      variance: -5000,
      progress: 15,
      startDate: '10/04/2024',
      endDate: '25/12/2024',
      health: 'Good',
      risks: 1,
      activities: 2,
      teamSize: 1,
      conversionProbability: 25
    }
  ]

  useEffect(() => {
    setSalesData(dummySalesData)
  }, [])

  // Calculate KPIs
  const calculateKPIs = () => {
    const totalLeads = salesData.length
    const activeLeads = salesData.filter(lead => !['Converted', 'Lost'].includes(lead.status)).length
    const convertedLeads = salesData.filter(lead => lead.status === 'Converted').length
    const totalValue = salesData.reduce((sum, lead) => sum + lead.leadValue, 0)
    const avgProgress = salesData.length > 0 ? Math.round(salesData.reduce((sum, lead) => sum + lead.progress, 0) / salesData.length) : 0

    return {
      totalLeads,
      activeLeads,
      convertedLeads,
      totalValue,
      avgProgress
    }
  }

  const kpis = calculateKPIs()

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleApplyFilter = () => {
    // Filter logic will be implemented here
    console.log('Applying filters:', filters)
  }

  const handleResetFilter = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      status: 'All Status',
      priority: 'All Priority',
      manager: 'All Managers'
    })
    setSearchTerm('')
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      'New': { color: 'bg-blue-100 text-blue-800', text: 'New' },
      'Contacted': { color: 'bg-yellow-100 text-yellow-800', text: 'Contacted' },
      'Qualified': { color: 'bg-purple-100 text-purple-800', text: 'Qualified' },
      'Demo Scheduled': { color: 'bg-indigo-100 text-indigo-800', text: 'Demo Scheduled' },
      'Proposal Sent': { color: 'bg-orange-100 text-orange-800', text: 'Proposal Sent' },
      'Negotiation': { color: 'bg-pink-100 text-pink-800', text: 'Negotiation' },
      'Converted': { color: 'bg-green-100 text-green-800', text: 'Converted' },
      'Lost': { color: 'bg-red-100 text-red-800', text: 'Lost' }
    }
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    )
  }

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'High': { color: 'bg-red-100 text-red-800', text: 'High' },
      'Medium': { color: 'bg-yellow-100 text-yellow-800', text: 'Medium' },
      'Low': { color: 'bg-green-100 text-green-800', text: 'Low' }
    }
    const config = priorityConfig[priority] || { color: 'bg-gray-100 text-gray-800', text: priority }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    )
  }

  const getHealthBadge = (health) => {
    const healthConfig = {
      'Excellent': { color: 'bg-green-100 text-green-800', text: 'Excellent' },
      'Good': { color: 'bg-blue-100 text-blue-800', text: 'Good' },
      'Fair': { color: 'bg-yellow-100 text-yellow-800', text: 'Fair' },
      'Poor': { color: 'bg-red-100 text-red-800', text: 'Poor' }
    }
    const config = healthConfig[health] || { color: 'bg-gray-100 text-gray-800', text: health }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    )
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  return (
    <div className="sales-dashboard" style={{ fontSize: '14px' }}>
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Sales Performance Report</h1>
          <p>Comprehensive sales analytics and performance insights</p>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-secondary">
            <Download size={16} />
            Export PDF
          </button>
          <button className="btn-primary">
            <Download size={16} />
            Export Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="form-section">
        <div className="section-header">
          <h3>Report Filters</h3>
          <p>Customize your sales performance analysis</p>
        </div>
        
        <div className="form-content">
          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">From Date *</label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">To Date *</label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="form-select"
              >
                <option value="All Status">All Status</option>
                <option value="New">New</option>
                <option value="Contacted">Contacted</option>
                <option value="Qualified">Qualified</option>
                <option value="Demo Scheduled">Demo Scheduled</option>
                <option value="Proposal Sent">Proposal Sent</option>
                <option value="Negotiation">Negotiation</option>
                <option value="Converted">Converted</option>
                <option value="Lost">Lost</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="form-select"
              >
                <option value="All Priority">All Priority</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>
          
          <div className="grid-row-2">
            <div className="form-group">
              <label className="form-label">Sales Manager</label>
              <select
                value={filters.manager}
                onChange={(e) => handleFilterChange('manager', e.target.value)}
                className="form-select"
              >
                <option value="All Managers">All Managers</option>
                <option value="Sarah Johnson">Sarah Johnson</option>
                <option value="Michael Chen">Michael Chen</option>
                <option value="Alex Rodriguez">Alex Rodriguez</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Search</label>
              <input
                type="text"
                placeholder="Search leads, prospects, or activities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button
              onClick={handleApplyFilter}
              className="btn-primary"
            >
              <Filter size={16} />
              Apply Filter
            </button>
            <button
              onClick={handleResetFilter}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset Filter
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <BarChart3 size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.totalLeads}</div>
          <div className="metric-title">Total Leads</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Activity size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.activeLeads}</div>
          <div className="metric-title">Active Leads</div>
          <div className="metric-subtitle">In pipeline</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.convertedLeads}</div>
          <div className="metric-title">Converted</div>
          <div className="metric-subtitle">Successfully closed</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(kpis.totalValue)}</div>
          <div className="metric-title">Total Value</div>
          <div className="metric-subtitle">Pipeline value</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="form-section">
        <div className="section-header" style={{ marginLeft: '20px' }}>
          <h3>Sales Pipeline Details</h3>
          <p>Detailed view of all leads and their current status</p>
        </div>
        
        <div className="form-content">
          <div className="data-table-container" style={{ fontSize: '13px' }}>
            <table className="data-table" style={{ fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Lead Name</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Priority</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Manager</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Lead Value</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Actual</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Variance</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Progress</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Start Date</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>End Date</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Health</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Risks</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Activities</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Team Size</th>
                </tr>
              </thead>
              <tbody>
                {salesData.map((lead) => (
                  <tr key={lead.id} style={{ fontSize: '13px' }}>
                    <td style={{ padding: '12px 8px', minWidth: '180px' }}>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '13px', lineHeight: '1.3' }}>{lead.leadName}</div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '2px' }}>{lead.id}</div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 8px' }}>{getStatusBadge(lead.status)}</td>
                    <td style={{ padding: '12px 8px' }}>{getPriorityBadge(lead.priority)}</td>
                    <td style={{ padding: '12px 8px', fontSize: '13px' }}>{lead.manager}</td>
                    <td style={{ padding: '12px 8px', fontWeight: '500', fontSize: '13px' }}>{formatCurrency(lead.leadValue)}</td>
                    <td style={{ padding: '12px 8px', fontSize: '13px' }}>{formatCurrency(lead.actualValue)}</td>
                    <td style={{ padding: '12px 8px', fontSize: '12px' }}>
                      <span className={lead.variance >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {lead.variance >= 0 ? '+' : ''}{formatCurrency(lead.variance)} {lead.variance >= 0 ? 'Over' : 'Under'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '60px', height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                          <div 
                            style={{ 
                              width: `${lead.progress}%`, 
                              height: '100%', 
                              backgroundColor: '#6A4CA2',
                              transition: 'width 0.3s ease'
                            }}
                          ></div>
                        </div>
                        <span style={{ fontSize: '12px', fontWeight: '500', minWidth: '35px' }}>{lead.progress}%</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}>{lead.startDate}</td>
                    <td style={{ padding: '12px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}>{lead.endDate}</td>
                    <td style={{ padding: '12px 8px' }}>{getHealthBadge(lead.health)}</td>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {lead.risks > 0 && <AlertTriangle size={12} className="text-red-500" />}
                        <span style={{ fontSize: '13px' }}>{lead.risks}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Activity size={12} className="text-blue-500" />
                        <span style={{ fontSize: '13px' }}>{lead.activities}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Users size={12} className="text-gray-500" />
                        <span style={{ fontSize: '13px' }}>{lead.teamSize}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Financial Summary Footer */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
          </div>
          <div className="metric-value">{formatCurrency(kpis.totalValue)}</div>
          <div className="metric-title">Total Lead Value</div>
          <div className="metric-subtitle">Pipeline potential</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
          </div>
          <div className="metric-value">
            {formatCurrency(salesData.reduce((sum, lead) => sum + lead.actualValue, 0))}
          </div>
          <div className="metric-title">Total Actual Value</div>
          <div className="metric-subtitle">Current value</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Target size={16} />
            </div>
          </div>
          <div className={`metric-value ${salesData.reduce((sum, lead) => sum + lead.variance, 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(salesData.reduce((sum, lead) => sum + lead.variance, 0))}
          </div>
          <div className="metric-title">Total Variance</div>
          <div className="metric-subtitle">Performance gap</div>
        </div>
      </div>
    </div>
  )
}

export default SalesPerformanceReport
